import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UXChromeProvider, useGlobalShortcuts } from "./contexts/UXChromeContext";
import CommandPalette from "./components/CommandPalette";
import ShortcutsModal from "./components/ShortcutsModal";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import Home from "./pages/Home";
import ThemeSettings from "./pages/ThemeSettings";
import SetupMfa from "./pages/SetupMfa";
import EmergencyRecovery from "./pages/EmergencyRecovery";
import ChangePassword from "./pages/ChangePassword";
import MasterFinance from "./pages/MasterFinance";
import ModuleDashboard from "./pages/ModuleDashboard";
import Dashboard from "./pages/Dashboard";
import Departments from "./pages/Departments";
import Users from "./pages/Users";
import Audit from "./pages/Audit";
import Forbidden from "./pages/Forbidden";
import Staff from "./pages/Staff";
import StaffProfile from "./pages/StaffProfile";
import AccountSettings from "./pages/AccountSettings";
import Contracts from "./pages/Contracts";
import WageRates from "./pages/WageRates";
import Attendance from "./pages/Attendance";
import AttendanceExceptions from "./pages/AttendanceExceptions";
import OTReview from "./pages/OTReview";
import BiometricDevices from "./pages/BiometricDevices";
import BiometricOps from "./pages/BiometricOps";
import ShiftRules from "./pages/ShiftRules";
import Payroll from "./pages/Payroll";
import PayrollPeriod from "./pages/PayrollPeriod";
import PayrollFunding from "./pages/PayrollFunding";
import Finance from "./pages/Finance";
import Reports from "./pages/Reports";
import { ROLES, WAGE_VIEW_ROLE_LIST } from "./lib/rbac";
import ModuleAppShell from "./components/ModuleAppShell";
import MasterFinanceShell from "./components/MasterFinanceShell";
import ModuleFinance from "./pages/ModuleFinance";
import ManagementFees from "./pages/ManagementFees";
import Leave from "./pages/Leave";
import Discipline from "./pages/Discipline";
import Incidents from "./pages/Incidents";
import IncidentsGlobal from "./pages/IncidentsGlobal";
import Tasks from "./pages/Tasks";
import AdminPermissions from "./pages/AdminPermissions";
import AdminSettings from "./pages/AdminSettings";
import AdminSecurity from "./pages/AdminSecurity";
import AdminAudit from "./pages/AdminAudit";
import AdminPayrollTrash from "./pages/AdminPayrollTrash";
import AdminNotifications from "./pages/AdminNotifications";
import DemoDataAdmin from "./pages/DemoDataAdmin";
import LeaveTypes from "./pages/LeaveTypes";
import LeaveAccrualConfig from "./pages/LeaveAccrualConfig";
import SickPayConfig from "./pages/SickPayConfig";
import SystemHealth from "./pages/SystemHealth";
import Projects from "./pages/Projects";
import PPE from "./pages/PPE";
import ErrorBoundary from "./components/ErrorBoundary";
import LegacyRedirect from "./components/LegacyRedirect";
import RequirePagePermission from "./components/RequirePagePermission";

function ModuleRoute({ children, title, perm }) {
    // Iter60.p48.1 — When a `perm` is supplied, wrap the module page in
    // a permission gate so blocked users (e.g. Attendance Officer
    // typing /m/ap/payroll directly) see a friendly banner instead of
    // a blank page + toast storm.
    return (
        <ModuleAppShell title={title}>
            <RequirePagePermission perm={perm}>{children}</RequirePagePermission>
        </ModuleAppShell>
    );
}

function GlobalChrome() {
    useGlobalShortcuts();
    return (
        <>
            <CommandPalette />
            <ShortcutsModal />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <ThemeProvider>
            <AuthProvider>
                <PermissionsProvider>
                <BrowserRouter>
                    <UXChromeProvider>
                        <GlobalChrome />
                        <ErrorBoundary>
                        <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/auth/emergency-recovery" element={<EmergencyRecovery />} />
                        <Route path="/change-password" element={<ChangePassword />} />
                        <Route path="/setup-mfa" element={<ProtectedRoute><SetupMfa /></ProtectedRoute>} />
                        <Route path="/forbidden" element={<Forbidden />} />
                        <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
                        <Route path="/settings/theme" element={<ProtectedRoute><ThemeSettings /></ProtectedRoute>} />
                        {/* Iter60.p55b — Global Incident Directory (HR + Admin only, top-level route). */}
                        <Route
                            path="/incidents"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER]}>
                                    <IncidentsGlobal />
                                </ProtectedRoute>
                            }
                        />

                        {/* New Home + Master Finance + Module Dashboards */}
                        <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                        <Route
                            path="/master-finance"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Master Finance"><MasterFinance /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/master-finance/payroll"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Payroll · All Modules"><Payroll /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/master-finance/funding"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Payroll Funding · All Modules"><PayrollFunding /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/master-finance/ledger"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Consolidated Ledger"><Finance /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/master-finance/management-fees"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Management Fees"><ManagementFees /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/master-finance/wage-rates"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Wage Rates Overview"><WageRates /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/master-finance/reports"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Finance Reports"><Reports /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/master-finance/audit"
                            element={
                                <ProtectedRoute>
                                    <MasterFinanceShell title="Audit · Finance"><Audit /></MasterFinanceShell>
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/m/:moduleCode" element={<ProtectedRoute><ModuleDashboard /></ProtectedRoute>} />

                        {/* Module sub-pages — render existing pages inside ModuleAppShell.
                            Existing pages' inner AppShell auto-collapses to children when ModuleScope context is set. */}
                        <Route path="/m/:moduleCode/staff" element={<ProtectedRoute><ModuleRoute title="Staff Directory"><Staff /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/staff/:id" element={<ProtectedRoute><ModuleRoute title="Employee Profile"><StaffProfile /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/attendance" element={<ProtectedRoute><ModuleRoute title="Attendance"><Attendance /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/attendance/exceptions" element={<ProtectedRoute><ModuleRoute title="Exception Queue"><AttendanceExceptions /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/attendance/ot-review" element={<ProtectedRoute><ModuleRoute title="OT Review"><OTReview /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/shift-rules" element={<ProtectedRoute><ModuleRoute title="Shift Rules & Holidays"><ShiftRules /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/projects" element={<ProtectedRoute><ModuleRoute title="Projects"><Projects /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/biometric-devices" element={<ProtectedRoute><ModuleRoute title="Biometric Devices"><BiometricDevices /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/biometric" element={<ProtectedRoute><ModuleRoute title="Biometric Ops"><BiometricOps /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/contracts" element={<ProtectedRoute><ModuleRoute title="Contracts" perm="contracts.view"><Contracts /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/ppe" element={<ProtectedRoute><ModuleRoute title="Clothing & PPE" perm="ppe.read"><PPE /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/wage-rates" element={<ProtectedRoute allowedRoles={WAGE_VIEW_ROLE_LIST}><ModuleRoute title="Wage Rates" perm="wage_rates.view"><WageRates /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/payroll" element={<ProtectedRoute><ModuleRoute title="Payroll" perm="payroll.view"><Payroll /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/payroll/funding" element={<ProtectedRoute><ModuleRoute title="Payroll Funding" perm="payroll_funding.write"><PayrollFunding /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/payroll/:id" element={<ProtectedRoute><ModuleRoute title="Payroll Period" perm="payroll.view"><PayrollPeriod /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/finance" element={<ProtectedRoute><ModuleFinance /></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/reports" element={<ProtectedRoute><ModuleRoute title="Reports" perm="reports.view"><Reports /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/leave" element={<ProtectedRoute><ModuleRoute title="Leave & Permissions"><Leave /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/discipline" element={<ProtectedRoute><ModuleRoute title="Discipline"><Discipline /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/incidents" element={<ProtectedRoute><ModuleRoute title="Incidents & Emergencies" perm="incidents.view"><Incidents /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/tasks" element={<ProtectedRoute><ModuleRoute title="Tasks"><Tasks /></ModuleRoute></ProtectedRoute>} />
                        <Route path="/m/:moduleCode/audit" element={<ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}><ModuleRoute title="Audit Log"><Audit /></ModuleRoute></ProtectedRoute>} />

                        {/* Admin dropdown routes (kept top-level) */}
                        <Route
                            path="/admin/settings/permissions"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <AdminPermissions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/settings/demo-data"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <DemoDataAdmin />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/admin/users" element={<Navigate to="/users" replace />} />
                        <Route
                            path="/admin/settings"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <AdminSettings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/settings/leave-types"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR]}>
                                    <LeaveTypes />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/settings/leave-accrual"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <LeaveAccrualConfig />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/settings/sick-pay"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <SickPayConfig />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/notifications"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <AdminNotifications />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/system-health"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <SystemHealth />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/security"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <AdminSecurity />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/audit"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <AdminAudit />
                                </ProtectedRoute>
                            }
                        />
                        {/* Iter84 — Payroll Trash for Super Admin only.
                            Backend endpoints already role-gate at
                            kaafi_system_admin, but ProtectedRoute here
                            keeps unauthorised nav out of the router. */}
                        <Route
                            path="/admin/payroll-trash"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <AdminPayrollTrash />
                                </ProtectedRoute>
                            }
                        />

                        {/* Legacy top-level routes redirect to home so users can't accidentally land on the un-shelled view */}
                        <Route path="/dashboard" element={<Navigate to="/" replace />} />
                        <Route path="/staff" element={<Navigate to="/" replace />} />
                        <Route path="/attendance" element={<Navigate to="/" replace />} />
                        <Route path="/attendance/exceptions" element={<Navigate to="/" replace />} />
                        <Route path="/shift-rules" element={<Navigate to="/" replace />} />
                        <Route path="/biometric-devices" element={<Navigate to="/" replace />} />
                        <Route path="/contracts" element={<Navigate to="/" replace />} />
                        <Route path="/wage-rates" element={<Navigate to="/" replace />} />
                        <Route path="/payroll/funding" element={<Navigate to="/" replace />} />
                        <Route path="/reports" element={<Navigate to="/" replace />} />
                        <Route path="/finance" element={<Navigate to="/master-finance" replace />} />
                        <Route path="/mobile-attendance" element={<Navigate to="/" replace />} />
                        <Route
                            path="/_legacy/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/departments"
                            element={
                                <ProtectedRoute>
                                    <Departments />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/staff"
                            element={
                                <ProtectedRoute>
                                    <Staff />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/staff/:id"
                            element={
                                <ProtectedRoute>
                                    <LegacyRedirect
                                        fetchPath={(id) => `/employees/${id}`}
                                        targetPath={(code, id) => `/m/${code}/staff/${id}`}
                                    />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/contracts"
                            element={
                                <ProtectedRoute>
                                    <Contracts />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/wage-rates"
                            element={
                                <ProtectedRoute allowedRoles={WAGE_VIEW_ROLE_LIST}>
                                    <WageRates />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/attendance"
                            element={
                                <ProtectedRoute>
                                    <Attendance />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/attendance/exceptions"
                            element={
                                <ProtectedRoute
                                    allowedRoles={[
                                        ROLES.KAAFI_ADMIN,
                                        ROLES.KAAFI_HR,
                                        ROLES.ASL_DEPT_MGR,
                                    ]}
                                >
                                    <AttendanceExceptions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/biometric-devices"
                            element={
                                <ProtectedRoute
                                    allowedRoles={[
                                        ROLES.KAAFI_ADMIN,
                                        ROLES.KAAFI_HR,
                                        ROLES.ASL_SENIOR,
                                        ROLES.ASL_DEPT_MGR,
                                    ]}
                                >
                                    <BiometricDevices />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/shift-rules"
                            element={
                                <ProtectedRoute
                                    allowedRoles={[
                                        ROLES.KAAFI_ADMIN,
                                        ROLES.KAAFI_HR,
                                        ROLES.ASL_SENIOR,
                                    ]}
                                >
                                    <ShiftRules />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR]}>
                                    <Users />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/audit"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN]}>
                                    <Audit />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/payroll"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_SENIOR, ROLES.ASL_FINANCE, ROLES.ASL_DEPT_MGR]}>
                                    <Payroll />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/payroll/periods/:id"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_SENIOR, ROLES.ASL_FINANCE, ROLES.ASL_DEPT_MGR]}>
                                    <LegacyRedirect
                                        fetchPath={(id) => `/payroll/periods/${id}`}
                                        targetPath={(code, id) => `/m/${code}/payroll/${id}`}
                                    />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/payroll/funding"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_SENIOR, ROLES.ASL_FINANCE]}>
                                    <PayrollFunding />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/finance"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_SENIOR, ROLES.ASL_FINANCE]}>
                                    <Finance />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports"
                            element={
                                <ProtectedRoute allowedRoles={[ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_SENIOR, ROLES.ASL_FINANCE, ROLES.ASL_DEPT_MGR, ROLES.ASL_VIEWER]}>
                                    <Reports />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                        </ErrorBoundary>
                    </UXChromeProvider>
                </BrowserRouter>
                <Toaster richColors position="top-right" />
                </PermissionsProvider>
            </AuthProvider>
            </ThemeProvider>
        </div>
    );
}

export default App;
